import { useState, useEffect } from 'react';
import { extensionService } from '../../services/extensionService';
import {
    Download,
    RefreshCw,
    Package,
    Calendar,
    HardDrive,
    Clock,
    CheckCircle,
    AlertCircle,
    Database,
    TrendingUp,
    ExternalLink,
    Video
} from 'lucide-react';

/**
 * Extension Manager Component
 * Displays latest extension version and admin cache controls
 */
export const ExtensionManager = () => {
    const [latestVersion, setLatestVersion] = useState(null);
    const [cacheStats, setCacheStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Load initial data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Always fetch latest version (public endpoint)
            const versionData = await extensionService.getLatestVersion();
            setLatestVersion(versionData);

            // Try to fetch cache stats (admin endpoint - may fail if not admin)
            try {
                const statsData = await extensionService.getCacheStats();
                setCacheStats(statsData);
            } catch (statsError) {
                // Admin endpoint failed - user might not be admin, or not logged in
                console.warn('Cache stats not available (admin only):', statsError);
                setCacheStats(null);
            }
        } catch (err) {
            console.error('Failed to fetch extension data:', err);
            setError(err.response?.data?.detail || 'Failed to load extension information');
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshCache = async () => {
        setRefreshing(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const result = await extensionService.refreshCache();
            setSuccessMessage(`Cache refreshed successfully! Latest version: ${result.latest_version}`);

            // Reload data after refresh
            await fetchData();
        } catch (err) {
            console.error('Failed to refresh cache:', err);
            setError(err.response?.data?.detail || 'Failed to refresh cache');
        } finally {
            setRefreshing(false);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeAgo = (seconds) => {
        if (seconds === null || seconds === undefined) return 'N/A';

        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h ago`;
        if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
        if (minutes > 0) return `${minutes}m ${seconds % 60}s ago`;
        return `${seconds}s ago`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading extension data...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Tutorial Prompt Banner */}
            <div className="p-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800">
                <div className="flex items-center gap-2 text-cyan-800 dark:text-cyan-200">
                    <Video className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">
                        <strong>New to Developer Mode?</strong> Watch the tutorial in the <strong>Tutorials tab</strong> above to learn how to install the extension using the ZIP file.
                    </span>
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{successMessage}</span>
                    <button onClick={() => setSuccessMessage(null)} className="ml-auto">
                        <span className="sr-only">Close</span>
                        ×
                    </button>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="ml-auto">
                        <span className="sr-only">Close</span>
                        ×
                    </button>
                </div>
            )}

            {/* Latest Version Card */}
            <div className="p-6 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-blue-500/10">
                            <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Latest Extension Version
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Current release information
                            </p>
                        </div>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium">
                        v{latestVersion?.version || 'N/A'}
                    </span>
                </div>

                {latestVersion && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-800/50">
                            <Calendar className="w-5 h-5 text-gray-500" />
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Published</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatDate(latestVersion.published_at)}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-800/50">
                            <HardDrive className="w-5 h-5 text-gray-500" />
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">File Size</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatBytes(latestVersion.file_size_bytes)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Release Notes */}
                {latestVersion?.release_notes && (
                    <div className="mt-4 p-4 rounded-lg bg-white/70 dark:bg-gray-800/70">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Release Notes
                        </h4>
                        <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line max-h-32 overflow-y-auto">
                            {latestVersion.release_notes}
                        </div>
                    </div>
                )}

                {/* Download Button */}
                {latestVersion?.download_url && (
                    <a
                        href={latestVersion.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors shadow-lg hover:shadow-xl"
                    >
                        <Download className="w-5 h-5" />
                        Download Extension v{latestVersion.version}
                        <ExternalLink className="w-4 h-4 ml-auto" />
                    </a>
                )}
            </div>

            {/* Cache Statistics - Only for Admin Users */}
            {!cacheStats && latestVersion && (
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200">
                    <p className="text-sm">
                        💡 <strong>Note:</strong> Cache statistics and refresh controls are only available for administrators.
                    </p>
                </div>
            )}

            {/* Cache Statistics */}
            {cacheStats && (
                <div className="p-6 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-purple-500/10">
                                <Database className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Cache Statistics
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Backend caching metrics
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleRefreshCache}
                            disabled={refreshing}
                            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing...' : 'Refresh Cache'}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                                <TrendingUp className="w-4 h-4 text-blue-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Cached Versions</span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {cacheStats.total_cached_versions || 0}
                            </div>
                        </div>

                        <div className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-4 h-4 text-orange-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Cache Age</span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {formatTimeAgo(cacheStats.cache_age_seconds)}
                            </div>
                        </div>

                        <div className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                                <RefreshCw className="w-4 h-4 text-green-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Next Refresh</span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {cacheStats.next_refresh_in_seconds !== null
                                    ? `${Math.floor(cacheStats.next_refresh_in_seconds / 60)}m`
                                    : 'N/A'
                                }
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>Cache TTL:</strong> {Math.floor((cacheStats.cache_ttl_seconds || 300) / 60)} minutes
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
